<template>
    <div>
        <base-dialog :loading="loading" :value="dialog" :width="660" :fullscreen="isMobile"
            :title="`${editMode ? $t('common.edit') : $t('common.create')} ${$t('common.template').toLowerCase()}`"
            @input="onInput" @close="hide">
            <div class="field-group">
                <label class="field-label">{{ $t('common.template_name') }}</label>
                <kimsa-text-field force-value dense :placeholder="$t('common.template_name')" @change="name = $event"
                    :value="name" />
            </div>

            <div class="info-banner">
                <v-icon size="15">mdi-information-outline</v-icon>
                <span>{{ $t('common.update_condition') }}</span>
            </div>

            <div class="metric-scroll">
                <div class="metric-grid">
                    <div class="metric-headers">
                        <span class="mh-num">#</span>
                        <span>{{ $t('common.evaluation_name') }}</span>
                        <span>{{ $t('common.unit_measure') }}</span>
                        <span>{{ $t('common.ideal_value') }}</span>
                        <span class="mh-pm">{{ $t('common.upgrade') }}</span>
                        <span />
                    </div>

                    <div v-for="(item, i) in config" :key="item.id" class="metric-row">
                        <span class="metric-num">{{ i + 1 }}</span>
                        <kimsa-text-field force-value dense :placeholder="$t('common.name')"
                            @change="config[i].name = $event" :value="config[i].name" />
                        <kimsa-combobox dense :items="units" @change="config[i].unit = $event" :value="config[i].unit"
                            no-object />
                        <kimsa-text-field force-value dense :placeholder="$t('common.ideal_value')"
                            @change="onChangeIdeal(i, $event)" :value="config[i].value" @keypress="idealValidation"
                            custom-keypress />
                        <div class="pm-group">
                            <button type="button" class="pm-btn" :class="{ active: config[i].upgrade === false }"
                                @click="config[i].upgrade = false">−</button>
                            <button type="button" class="pm-btn" :class="{ active: config[i].upgrade === true }"
                                @click="config[i].upgrade = true">+</button>
                        </div>
                        <button type="button" class="metric-del" :class="{ hidden: i === 0 }" :disabled="i === 0"
                            @click="deleteRow(i)">
                            <v-icon size="15">$delete</v-icon>
                        </button>
                    </div>
                </div>
            </div>

            <button type="button" class="add-metric-btn" @click="addNewRow">
                <v-icon size="14">mdi-plus</v-icon>
                {{ $t('common.add_new_evaluation') }}
            </button>

            <template #footer>
                <dialog-actions :confirm-label="editMode ? $t('common.edit') : $t('common.create')" :disabled="disabled"
                    :loading="loading" @cancel="hide" @confirm="save" />
            </template>
        </base-dialog>
    </div>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import { db } from "@/utils/Firebase";
import { getEvaluationUnits } from "@/utils/LocalData";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { randomString } from "@/utils/Common";
import BaseDialog from '@/components/common/BaseDialog';
import DialogActions from '@/components/common/DialogActions';

export default {
    name: "CreateEvaluationDialog",
    components: { KimsaCombobox, KimsaTextField, BaseDialog, DialogActions },
    props: {
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            editMode: false,
            units: getEvaluationUnits(),
            configTemplate: { name: '', unit: 'none', value: 0, upgrade: true },
            name: '',
            config: [{ id: randomString(12), name: '', unit: 'none', value: 0, upgrade: true }],
            evaluation: null,
        }
    },
    computed: {
        disabled(vm = this) {
            if (vm.name.length < 3) return true
            return !vm.config?.every(c => c.name)
        }
    },
    methods: {
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.evaluation = null
            vm.name = ''
            vm.config = [{ ...vm.configTemplate, id: randomString(12) }]
            vm.editMode = false
        },
        loadEvaluation(evaluation) {
            const vm = this
            vm.editMode = true
            vm.evaluation = evaluation
            vm.name = evaluation.name
            vm.config = (evaluation.config || []).map(c => ({ ...c }))
            vm.show()
        },
        addNewRow() {
            const vm = this
            vm.config.push({ ...vm.configTemplate, id: randomString(12) })
        },
        deleteRow(i) {
            const vm = this
            vm.config.splice(i, 1)
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
                if (vm.editMode) {
                    await db.collection('evaluations').doc(vm.evaluation.id).update({
                        name: vm.name,
                        config: vm.config
                    })
                    vm.toast(vm.$t('common.template_updated'))
                } else {
                    await db.collection('evaluations').add({
                        coachId: vm.coachId,
                        name: vm.name,
                        config: vm.config,
                    })
                    vm.toast(vm.$t('common.template_created'))
                }

                vm.hide()
            })
        },
        onChangeIdeal(i, value) {
            const vm = this
            vm.config[i].value = parseFloat(value.toString().replace(/,/g, '.'))
        },
        idealValidation(event) {
            const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '-', ',', '.']
            if (!allowedKeys.includes(event.key) && event.key !== 'Backspace') event.preventDefault()
        },
    },
}
</script>

<style scoped>
.field-group {
    margin-bottom: 16px;
}

.field-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    margin-bottom: 6px;
}

.info-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
    padding: 10px 12px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    font-size: 12px;
    line-height: 1.4;
    color: var(--v-txtMuted-base);
}

.info-banner .v-icon {
    color: var(--v-accent-base);
    margin-top: 1px;
    flex-shrink: 0;
}

.metric-scroll {
    overflow-x: auto;
}

.metric-grid {
    min-width: 560px;
}

.metric-headers,
.metric-row {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) 108px 84px 74px 28px;
    gap: 8px;
    align-items: center;
}

.metric-headers {
    margin-bottom: 4px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-txtMuted-base);
}

.metric-row {
    margin-bottom: 6px;
}

.metric-row > * {
    min-width: 0;
}

.metric-num,
.mh-num {
    text-align: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.mh-pm {
    text-align: center;
}

.pm-group {
    display: flex;
    height: 34px;
    border: 1px solid var(--v-outline2-base);
    border-radius: 8px;
    overflow: hidden;
}

.pm-btn {
    flex: 1;
    background: var(--v-surface-base);
    border: none;
    color: var(--v-txtMuted-base);
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
}

.pm-btn:first-child {
    border-right: 1px solid var(--v-outline2-base);
}

.pm-btn.active {
    background: var(--v-accent-base);
    color: #FFFFFF;
}

.metric-del {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 7px;
    border: none;
    background: transparent;
    color: var(--v-txtMuted-base);
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
}

.metric-del:hover {
    background: rgba(232, 92, 92, 0.12);
    color: var(--v-error-base);
}

.metric-del.hidden {
    visibility: hidden;
}

.add-metric-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    height: 38px;
    margin-top: 12px;
    border-radius: 8px;
    border: 1px solid rgba(51, 153, 153, 0.35);
    background: rgba(51, 153, 153, 0.08);
    color: var(--v-accent-base);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
}

.add-metric-btn:hover {
    background: rgba(51, 153, 153, 0.16);
}

.add-metric-btn .v-icon {
    color: inherit !important;
}
</style>
